<!-- CreateShift.vue -->
<template>
    <CustomDialog v-model="localVisible" :default-title="shiftTitle">
        <!-- CONTENT -->
        <template #content>
            <div>
                <!-- Employee Select -->
                <div v-if="showEmployeeSelect" class="ppv2-space-y-3">
                    <BaseSelect
                        v-if="!isEditingShift"
                        v-model="shiftForm.user_id"
                        :options="staffResouerceList"
                        :label="$t('staff_planning_v3.components.create_shift.employee')"
                        :placeholder="$t('staff_planning_v3.components.create_shift.select_employee')"
                        custom-label-class="poppinsSemiBold text-uppercase !ppv2-text-[#A09DBC]"
                        :custom-size-label="10"
                        fixed-dropdown
                    />
                </div>

                <!-- PLANNING -->
                <div class="ppv2-space-y-4">
                    <div class="v3-section-title mb-2">
                        <span>{{ $t('staff_planning_v3.components.create_shift.planning') }}</span>
                    </div>

                    <PlanningPeriodSection
                        :date="shiftForm.date"
                        @update:date="shiftForm.date = $event"
                        :periods="shiftForm.periods"
                        @update:periods="shiftForm.periods = $event"
                        :allowMultiple="true"
                        :maxPeriods="2"
                        :labels="{
                            date: $t('staff_planning_v3.components.create_shift.date'),
                            startTime: $t('staff_planning_v3.components.create_shift.start_time'),
                            endTime: $t('staff_planning_v3.components.create_shift.end_time'),
                            addSchedule: $t('staff_planning_v3.components.create_shift.add_schedule'),
                        }"
                    />
                </div>

                <!-- SECTION: OPTIONS AVANCÉES -->
                <div class="ppv2-space-y-3">
                    <div class="v3-section-title mb-2">
                        <span>{{ $t('staff_planning_v3.components.create_shift.advanced_options') }}</span>
                    </div>

                    <div class="ppv2-space-y-3 !ppv2-mb-4">
                        <!-- Recurrence -->
                        <RecurrenceAccordion
                            v-model="localRecurrenceOpen"
                            :frequency="shiftForm.frequency"
                            @update:frequency="shiftForm.frequency = $event"
                            :recurrenceEndDate="shiftForm.recurrence_end_date"
                            @update:recurrenceEndDate="shiftForm.recurrence_end_date = $event"
                            :selectedDays="shiftForm.recurrence_days || []"
                            :days="days"
                            :frequencyOptions="frequencyOptions"
                            :chooseDaysText="$t('staff_planning_v3.components.create_shift.choose_days')"
                            @toggle-day="toggleDay"
                        />

                        <!-- Copy Accordion -->
                        <CollapsibleAccordion :title="$t('staff_planning_v3.components.create_shift.copy_planning')">
                            <!-- COPIER DEPUIS -->
                            <div>
                                <label
                                    class="ppv2-block ppv2-text-[10px] poppinsBold ppv2-text-[#94A3B8] ppv2-uppercase ppv2-tracking-wider ppv2-mb-1.5"
                                    >{{ $t('staff_planning_v3.components.create_shift.copy_from') }}</label
                                >
                                <BaseSelect
                                    v-model="shiftForm.copy_from_staff_id"
                                    :options="staffResouerceList"
                                    :placeholder="$t('staff_planning_v3.components.create_shift.select_employee')"
                                    fixed-dropdown
                                />
                            </div>

                            <!-- PÉRIODE À COPIER -->
                            <div>
                                <label
                                    class="ppv2-block ppv2-text-[10px] poppinsBold ppv2-text-[#94A3B8] ppv2-uppercase ppv2-tracking-wider ppv2-mb-1.5"
                                    >{{ $t('staff_planning_v3.components.create_shift.copy_period') }}</label
                                >
                                <BaseSelect
                                    v-model="shiftForm.copy_from_week"
                                    :options="copyWeekOptions"
                                    option-label="label"
                                    option-value="value"
                                    :searchable="true"
                                    :multiple="true"
                                    :placeholder="$t('staff_planning_v3.components.create_shift.select_week')"
                                    fixed-dropdown
                                />
                            </div>
                        </CollapsibleAccordion>
                    </div>
                </div>

                <!-- NOTE -->
                <NoteSection v-model="shiftForm.note" />
            </div>
        </template>

        <!-- ACTIONS -->
        <template #actions>
            <ModalActions
                @cancel="closeModal"
                @confirm="$emit('save')"
                :confirmLabel="
                    isEditingShift
                        ? $t('staff_planning_v3.components.create_shift.edit_shift')
                        : $t('staff_planning_v3.components.create_shift.create_shift')
                "
                :disabled="false"
            />
        </template>
    </CustomDialog>
</template>

<script>
import { Plus, Minus } from 'lucide-vue'

import CustomDialog from '../../../Shared/CustomDialog.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseDateInput from '../../../Shared/BaseDateInput.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BaseTimeInput from '../../../Shared/BaseTimeInput.vue'
import NoteSection from './NoteSection.vue'
import RecurrenceAccordion from './RecurrenceAccordion.vue'
import CollapsibleAccordion from './CollapsibleAccordion.vue'
import PlanningPeriodSection from './PlanningPeriodSection.vue'
import ModalActions from './ModalActions.vue'

export default {
    name: 'CreateShift',

    components: {
        Plus,
        Minus,
        CustomDialog,
        BaseSelect,
        BaseDateInput,
        BaseButton,
        BaseTimeInput,
        NoteSection,
        RecurrenceAccordion,
        CollapsibleAccordion,
        PlanningPeriodSection,
        ModalActions,
    },

    props: {
        value: { type: Boolean, default: false },
        shiftForm: { type: Object, required: true },
        isEditingShift: { type: Boolean, default: false },
        showEmployeeSelect: { type: Boolean, default: false },
        selectedWeek: { type: String, required: true },
        staffResouerceList: { type: Array, required: true },
        showRecurrenceAccordion: { type: Boolean, default: false },
    },

    emits: ['input', 'save', 'update:showRecurrenceAccordion'],

    data() {
        return {
            frequencyOptions: [
                { label: this.$t('staff_planning_v3.components.create_shift.every_week'), value: 'Toutes les semaines' },
                { label: this.$t('staff_planning_v3.components.create_shift.every_2_weeks'), value: 'Toutes les 2 semaines' },
                { label: this.$t('staff_planning_v3.components.create_shift.every_month'), value: 'Tous les mois' },
            ],
            days: [
                { id: 'Monday', label: 'L' },
                { id: 'Tuesday', label: 'M' },
                { id: 'Wednesday', label: 'M' },
                { id: 'Thursday', label: 'J' },
                { id: 'Friday', label: 'V' },
                { id: 'Saturday', label: 'S' },
                { id: 'Sunday', label: 'D' },
            ],
        }
    },

    computed: {
        localRecurrenceOpen: {
            get() {
                return this.showRecurrenceAccordion
            },
            set(val) {
                this.$emit('update:showRecurrenceAccordion', val)
            },
        },
        shiftTitle() {
            const action = this.isEditingShift
                ? this.$t('staff_planning_v3.components.create_shift.title_edit')
                : this.$t('staff_planning_v3.components.create_shift.title_create')
            const forText = this.showEmployeeSelect
                ? ''
                : ` ${this.$t('staff_planning_v3.components.create_shift.for')} ${this.shiftForm.employee_name}`
            return `${action}${forText}`
        },
        localVisible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },

        hasSupEqualStartEnd() {
            return this.shiftForm.periods.some((period) => period.start >= period.end)
        },

        copyWeekOptions() {
            const options = []

            const [currentYear, currentWeekStr] = this.selectedWeek.split('-W')

            let year = parseInt(currentYear)

            // Add all weeks from week 1 to week 52 for the current year
            for (let week = 1; week <= 52; week++) {
                options.push({
                    value: `${year}-W${String(week).padStart(2, '0')}`,
                    label: `${this.$t('staff_planning_v3.components.create_shift.week')} ${week}`,
                })
            }

            return options
        },
    },

    methods: {
        toggleDay(dayId) {
            if (!this.shiftForm.recurrence_days) {
                this.$set(this.shiftForm, 'recurrence_days', [])
            }

            const days = this.shiftForm.recurrence_days

            if (days.includes(dayId)) {
                this.shiftForm.recurrence_days = days.filter((d) => d !== dayId)
            } else {
                this.shiftForm.recurrence_days.push(dayId)
            }
        },

        closeModal() {
            this.$emit('input', false)
        },
    },
}
</script>
